import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * `<complete-the-look>` — bundle upsell shown in the product details.
 *
 * Renders a list of "bundle_with" products (see complete-the-look.liquid) each
 * with a variant dropdown and a checkbox. It does NOT own the Add to cart button:
 * the theme's product form still adds the main product on its own. This component
 * only *supplements* that flow by:
 *   1. Relabelling the main button ("Add Both to Cart" / "Add All to Cart").
 *   2. Adding the checked bundle variants in a second /cart/add.js request when
 *      the product form is submitted.
 *
 * In "Make bundle" mode (`data-bundle`) there are no checkboxes: the main product
 * ("This item", kept in sync with the product form's variant) and every add-on are
 * shown as one set with its own "Add Set to Cart" button and running total, and the
 * main Add to cart button is left alone.
 *
 * @typedef {{ id: number, quantity: number }} LineItem
 * @extends {Component}
 */
class CompleteTheLook extends Component {
  requiredRefs = [];

  /** @type {HTMLElement | Document} */
  #scope = document;
  /** @type {HTMLFormElement | null} */
  #productForm = null;
  /** Default Add to cart label, captured before we relabel it. @type {string} */
  #defaultLabel = '';
  /** Prevents overlapping add requests. @type {boolean} */
  #adding = false;

  /** "Make bundle" mode. @returns {boolean} */
  get #isBundle() {
    return this.hasAttribute('data-bundle');
  }

  connectedCallback() {
    super.connectedCallback();

    // Work within the surrounding product section so we target the *main* product form.
    this.#scope = this.closest('.shopify-section') ?? document;

    // Scope to the main product's form by id so we don't grab a recommendation
    // card's quick-add form (which can also live in this section).
    const productId = this.dataset.productId;
    this.#productForm =
      (productId &&
        this.#scope.querySelector(`product-form-component[data-product-id="${productId}"] form`)) ||
      this.#scope.querySelector('product-form-component form');

    const content = this.#labelContent;
    if (content) this.#defaultLabel = content.textContent?.trim() ?? '';

    // Add the checked bundle items alongside the theme's own main-product add.
    this.#productForm?.addEventListener('submit', this.#onProductFormSubmit);

    // The theme morphs (re-renders) the button on variant change, which wipes our
    // label — re-apply it once that has happened.
    this.#scope.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    // Size / color changes inside the cards change the set's total.
    this.addEventListener('ctl:change', this.#updateBundleTotal);
    this.#updateBundleTotal();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#productForm?.removeEventListener('submit', this.#onProductFormSubmit);
    this.#scope.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.removeEventListener('ctl:change', this.#updateBundleTotal);
  }

  /** Checkbox change handler (bound declaratively via `on:change`). */
  handleToggle() {
    this.#applyButtonLabel();
  }

  /**
   * Color swatch click handler (bound via `on:click`). Each color is pre-rendered as its own
   * `.ctl-card__panel`; this shows the chosen one and hides the rest. The selected variant
   * lives on the visible panel, so the cart logic picks it up with no extra bookkeeping.
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  selectColor(event) {
    const swatch = event.target;
    const card = swatch.closest('.ctl-card');
    if (!(card instanceof HTMLElement)) return;

    const index = Number(swatch.dataset.colorIndex);
    const panels = card.querySelectorAll('.ctl-card__panel');
    const panel = panels[index];
    if (!(panel instanceof HTMLElement)) return;

    panels.forEach((item, i) => {
      if (item instanceof HTMLElement) item.hidden = i !== index;
    });

    card.querySelectorAll('.ctl-swatch').forEach((item) => {
      const active = item === swatch;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });

    const checkbox = card.querySelector('.ctl-card__checkbox');
    if (checkbox && panel.dataset.title) {
      checkbox.setAttribute('aria-label', `Add ${panel.dataset.title} to cart`);
    }

    this.#updateBundleTotal();
  }

  /** "Add Set to Cart" click handler (bound via `on:click`). Adds the main product + every add-on. */
  async addSet() {
    const button = this.#setButton;
    if (!button || button.disabled || this.#adding) return;

    // The product form's hidden id input is the source of truth for the main variant.
    const mainId = Number(
      this.#productForm?.querySelector('input[name="id"]')?.value ||
        this.#mainPanel?.dataset.variantId
    );
    if (!mainId) return;

    button.setAttribute('aria-busy', 'true');
    await this.#addItems([{ id: mainId, quantity: 1 }, ...this.#selectedItems()]);
    button.removeAttribute('aria-busy');
  }

  /** @param {Event & { detail?: { resource?: any, data?: { productId?: string } } }} event */
  #onVariantUpdate = (event) => {
    if (this.#isBundle) {
      this.#syncMainItem(event.detail);
      return;
    }

    // Re-apply after the theme finishes morphing the button.
    requestAnimationFrame(() => this.#applyButtonLabel());
  };

  /** @param {SubmitEvent} _event */
  #onProductFormSubmit = (_event) => {
    // In bundle mode the main button only adds the main product; the set has its own button.
    if (this.#isBundle) return;

    const items = this.#selectedItems();
    if (!items.length) return; // nothing checked → leave the normal single add alone

    // If the main product can't be added (sold out / invalid), don't add the bundle.
    if (this.#addToCartButton?.disabled) return;

    this.#addItems(items);
  };

  /**
   * Collects the currently checked bundle products and their selected variants.
   * @returns {LineItem[]}
   */
  #selectedItems() {
    // Bundle mode: every add-on. Otherwise: the checked ones.
    const cards = this.#isBundle
      ? this.querySelectorAll('.ctl-card:not(.ctl-card--main)')
      : Array.from(this.querySelectorAll('.ctl-card__checkbox:checked'), (checkbox) =>
          checkbox.closest('.ctl-card')
        );
    /** @type {LineItem[]} */
    const items = [];

    cards.forEach((card) => {
      // The visible panel is the color the shopper picked (there's only one without swatches).
      const panel = card?.querySelector('.ctl-card__panel:not([hidden])');
      const variantId = panel instanceof HTMLElement ? Number(panel.dataset.variantId) : NaN;
      if (variantId) items.push({ id: variantId, quantity: 1 });
    });

    return items;
  }

  /**
   * Mirrors the product form's newly selected variant into the "This item" row.
   * @param {{ resource?: any, data?: { productId?: string } } | undefined} detail
   */
  #syncMainItem(detail) {
    const variant = detail?.resource;
    const panel = this.#mainPanel;
    if (!variant || !panel) return;
    if (detail?.data?.productId && String(detail.data.productId) !== this.dataset.productId) return;

    panel.dataset.variantId = String(variant.id);
    panel.dataset.priceCents = String(variant.price);
    panel.dataset.available = String(variant.available);

    const summary = panel.querySelector('.ctl-card__summary');
    if (summary && Array.isArray(variant.options))
      summary.textContent = variant.options.join(' · ');

    const image = panel.querySelector('.ctl-card__media img');
    const src = variant.featured_media?.preview_image?.src;
    if (image instanceof HTMLImageElement && src) {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', '160');
      image.src = url.toString();
    }

    this.#setPanelPrice(panel, variant.price);
    this.#updateBundleTotal();
  }

  /**
   * Writes a price (in cents) into a panel, as plain text or as original + sale.
   * @param {HTMLElement} panel
   * @param {number} cents
   */
  #setPanelPrice(panel, cents) {
    const original = panel.querySelector('.ctl-card__price-original');
    const sale = panel.querySelector('.ctl-card__price-sale');

    if (original && sale) {
      original.textContent = this.#formatMoney(cents);
      sale.textContent = this.#formatMoney(this.#discounted(cents));
      return;
    }

    const priceEl = panel.querySelector('.ctl-card__price');
    if (priceEl) priceEl.textContent = this.#formatMoney(cents);
  }

  /** Recomputes the "Add Set to Cart" total and enabled state. Bundle mode only. */
  #updateBundleTotal = () => {
    const button = this.#setButton;
    const totalEl = this.querySelector('.ctl-bundle__submit-total');
    if (!button || !totalEl) return;

    let total = 0;
    let saleTotal = 0;
    this.querySelectorAll('.ctl-card__panel:not([hidden])').forEach((panel) => {
      const cents = panel instanceof HTMLElement ? Number(panel.dataset.priceCents) || 0 : 0;
      total += cents;
      saleTotal += this.#discounted(cents);
    });

    totalEl.replaceChildren('– ');
    if (saleTotal < total) {
      const original = document.createElement('s');
      original.textContent = this.#formatMoney(total);
      totalEl.append(original, ' ', this.#formatMoney(saleTotal));
    } else {
      totalEl.append(this.#formatMoney(total));
    }

    // Can't sell the set if the main product's selected variant is sold out.
    button.disabled = this.#mainPanel?.dataset.available === 'false';
  };

  /** @param {number} cents @returns {number} */
  #discounted(cents) {
    const percent = Number(this.dataset.discountPercent) || 0;
    // Round fractional cents up, matching the Liquid-rendered sale prices.
    return Math.ceil((cents * (100 - percent)) / 100);
  }

  /** @param {number} cents @returns {string} */
  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /** @returns {HTMLButtonElement | null} */
  get #setButton() {
    return this.querySelector('.ctl-bundle__submit');
  }

  /** The "This item" row's panel. @returns {HTMLElement | null} */
  get #mainPanel() {
    return this.querySelector('.ctl-card--main .ctl-card__panel');
  }

  /**
   * Adds the checked bundle items to the cart and refreshes the cart UI.
   * @param {LineItem[]} items
   */
  async #addItems(items) {
    if (this.#adding) return;
    this.#adding = true;

    // Mirror the theme: ask for the cart sections so the drawer/page can morph.
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const body = JSON.stringify({ items, sections: sections.join(',') });

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body })).then(
        (res) => res.json()
      );

      if (response.status) {
        console.error('Complete the look: failed to add bundle items', response);
        return;
      }

      // Let the cart drawer open and the cart sections re-render.
      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'complete-the-look', {
          source: 'complete-the-look',
          sections: response.sections,
        })
      );
    } catch (error) {
      console.error('Complete the look: error adding bundle items', error);
    } finally {
      this.#adding = false;
    }
  }

  /** The main product's Add to cart button. @returns {HTMLButtonElement | null} */
  get #addToCartButton() {
    return this.#productForm?.querySelector('button.add-to-cart-button') ?? null;
  }

  /** The text node inside the Add to cart button. @returns {HTMLElement | null} */
  get #labelContent() {
    return this.#addToCartButton?.querySelector('.add-to-cart-text__content') ?? null;
  }

  /** Updates the Add to cart label based on how many bundle items are checked. */
  #applyButtonLabel() {
    const button = this.#addToCartButton;
    const content = this.#labelContent;
    if (!button || !content) return;

    // Don't relabel a sold-out / disabled button.
    if (button.disabled) return;

    const count = this.querySelectorAll('.ctl-card__checkbox:checked').length;

    if (count === 0) {
      content.textContent = this.#defaultLabel;
    } else if (count === 1) {
      content.textContent = this.dataset.addBothText || 'Add Both to Cart';
    } else {
      content.textContent = this.dataset.addAllText || 'Add All to Cart';
    }
  }
}

if (!customElements.get('complete-the-look')) {
  customElements.define('complete-the-look', CompleteTheLook);
}

/**
 * `<ctl-variant-picker>` — a styled, accessible replacement for the native
 * variant `<select>` used in the bundle cards. Renders a trigger button and a
 * popup `role="listbox"`, so the open list can be themed (the native option
 * list can't be). On selection it writes the chosen variant id + price back to
 * its color's `.ctl-card__panel`, which the cart logic in `<complete-the-look>`
 * reads from.
 *
 * @extends {Component}
 */
class CtlVariantPicker extends Component {
  requiredRefs = [];

  #open = false;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /** @returns {HTMLButtonElement | null} */
  get #trigger() {
    return this.querySelector('.ctl-select__trigger');
  }

  /** @returns {HTMLElement | null} */
  get #list() {
    return this.querySelector('.ctl-select__list');
  }

  /** @returns {HTMLElement | null} */
  get #valueEl() {
    return this.querySelector('.ctl-select__value');
  }

  /** @returns {HTMLElement[]} */
  get #options() {
    return Array.from(this.querySelectorAll('.ctl-select__option'));
  }

  /** @returns {HTMLElement[]} */
  get #selectableOptions() {
    return this.#options.filter((option) => option.getAttribute('aria-disabled') !== 'true');
  }

  /** @returns {HTMLElement | null} */
  get #activeOption() {
    return this.querySelector('.ctl-select__option.is-active');
  }

  /** Trigger click handler (bound via `on:click`). */
  toggle() {
    this.#open ? this.#close({ focusTrigger: true }) : this.#openList();
  }

  /**
   * Option click handler (bound via `on:click`).
   * @param {MouseEvent & { target: HTMLElement }} event
   */
  choose(event) {
    const option = event.target.closest('.ctl-select__option');
    if (!(option instanceof HTMLElement) || option.getAttribute('aria-disabled') === 'true') return;

    this.#select(option);
    this.#close({ focusTrigger: true });
  }

  /**
   * Keyboard handler for the trigger and the list (bound via `on:keydown`).
   * @param {KeyboardEvent} event
   */
  onKeydown(event) {
    const { key } = event;

    if (!this.#open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        this.#openList();
      }
      return;
    }

    switch (key) {
      case 'Escape':
        event.preventDefault();
        this.#close({ focusTrigger: true });
        break;
      case 'Tab':
        this.#close();
        break;
      case 'Enter':
      case ' ': {
        event.preventDefault();
        const active = this.#activeOption;
        if (active && active.getAttribute('aria-disabled') !== 'true') {
          this.#select(active);
          this.#close({ focusTrigger: true });
        }
        break;
      }
      case 'ArrowDown':
      case 'ArrowUp':
      case 'Home':
      case 'End':
        event.preventDefault();
        this.#moveActive(key);
        break;
      default:
        break;
    }
  }

  #openList() {
    const list = this.#list;
    if (!list) return;

    this.#open = true;
    list.hidden = false;
    this.#trigger?.setAttribute('aria-expanded', 'true');

    const current =
      this.#options.find(
        (option) =>
          option.getAttribute('aria-selected') === 'true' &&
          option.getAttribute('aria-disabled') !== 'true'
      ) ?? this.#selectableOptions[0];
    this.#setActive(current);
    list.focus();
  }

  /** @param {{ focusTrigger?: boolean }} [options] */
  #close({ focusTrigger = false } = {}) {
    const list = this.#list;
    if (list) list.hidden = true;

    this.#open = false;
    this.#trigger?.setAttribute('aria-expanded', 'false');
    this.#clearActive();

    if (focusTrigger) this.#trigger?.focus();
  }

  /** @param {HTMLElement} option */
  #select(option) {
    for (const item of this.#options) {
      item.setAttribute('aria-selected', item === option ? 'true' : 'false');
    }

    const label = option.querySelector('.ctl-select__option-label')?.textContent?.trim() ?? '';
    if (this.#valueEl) this.#valueEl.textContent = label;

    const panel = this.closest('.ctl-card__panel');
    if (panel instanceof HTMLElement) {
      if (option.dataset.variantId) panel.dataset.variantId = option.dataset.variantId;
      if (option.dataset.priceCents) panel.dataset.priceCents = option.dataset.priceCents;
      this.#updatePrice(panel, option);
      this.dispatchEvent(new Event('ctl:change', { bubbles: true }));
    }
  }

  /**
   * Writes the chosen variant's price into its panel. With a block discount set, the price is
   * split into a crossed-out original and a sale price; otherwise it's plain text.
   * @param {HTMLElement} panel
   * @param {HTMLElement} option
   */
  #updatePrice(panel, option) {
    const { price, salePrice } = option.dataset;
    if (!price) return;

    const original = panel.querySelector('.ctl-card__price-original');
    const sale = panel.querySelector('.ctl-card__price-sale');

    if (original && sale && salePrice) {
      original.textContent = price;
      sale.textContent = salePrice;
      return;
    }

    const priceEl = panel.querySelector('.ctl-card__price');
    if (priceEl) priceEl.textContent = price;
  }

  /** @param {string} key */
  #moveActive(key) {
    const options = this.#selectableOptions;
    if (!options.length) return;

    let index = this.#activeOption ? options.indexOf(this.#activeOption) : -1;

    if (key === 'Home') index = 0;
    else if (key === 'End') index = options.length - 1;
    else if (key === 'ArrowDown') index = index < 0 ? 0 : Math.min(index + 1, options.length - 1);
    else if (key === 'ArrowUp') index = index < 0 ? options.length - 1 : Math.max(index - 1, 0);

    this.#setActive(options[index]);
  }

  /** @param {HTMLElement | undefined | null} option */
  #setActive(option) {
    this.#clearActive();
    if (!option) return;

    option.classList.add('is-active');
    if (option.id) this.#list?.setAttribute('aria-activedescendant', option.id);
    option.scrollIntoView({ block: 'nearest' });
  }

  #clearActive() {
    this.#activeOption?.classList.remove('is-active');
    this.#list?.removeAttribute('aria-activedescendant');
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (this.#open && event.target instanceof Node && !this.contains(event.target)) {
      this.#close();
    }
  };
}

if (!customElements.get('ctl-variant-picker')) {
  customElements.define('ctl-variant-picker', CtlVariantPicker);
}
